import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { forgotPassword, clearError } from '../../store/slices/authSlice';
import toast from 'react-hot-toast';

const ForgotPasswordPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  // Determine if this is an Airliner page based on URL
  const isAirliner = location.pathname.includes('/airliner');
  const resetPasswordPath = isAirliner ? '/airliner/auth/reset-password' : '/reset-password';
  const backPath = isAirliner ? '/airliner/auth/login' : '/';

  React.useEffect(() => {
    // Clear any previous errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    const validationErrors = {};
    
    if (!email.trim()) {
      validationErrors.email = 'Please enter an email address';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      validationErrors.email = 'Please enter a valid email address';
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setErrors({});
    
    try {
      const result = await dispatch(forgotPassword({ email })).unwrap();
      
      // If we get a token in the response, redirect directly to reset password page
      if (result.data && result.data.token) {
        const resetUrl = `${resetPasswordPath}?token=${result.data.token}&email=${encodeURIComponent(result.data.email)}`;
        toast.success('Redirecting to reset password...');
        navigate(resetUrl);
      } else {
        toast.success('Password reset link sent to your email');
      }
    } catch (err) {
      console.error('Failed to send reset email:', err);
      // Show a specific message for unverified email accounts
      if (typeof err === 'string' && err.toLowerCase().includes('not been verified')) {
        toast.error('Your email address has not been verified. Please check your inbox for the verification email.');
      } else {
        toast.error(err || 'Failed to send reset email');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-jet-background px-4 pt-20">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-jet-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-jet-orange" />
          </div>
          <h2 className="text-3xl font-semibold text-jet-primary mb-2">Forgot Password?</h2>
          <p className="text-gray-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-jet-primary mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email.toUpperCase()}
              onChange={(e) => setEmail(e.target.value)}
              style={{ textTransform: 'uppercase' }}
              placeholder="Enter your email"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jet-yellow focus:border-transparent transition-all"
              required
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-jet-yellow hover:bg-jet-yellow/90 text-jet-primary rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-jet-primary/30 border-t-jet-primary rounded-full animate-spin"></div>
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={backPath}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-jet-orange transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {isAirliner ? 'Login' : 'Home'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
